import { Map as ImmutableMap } from 'immutable';
import { createSelector, Selector } from '@reduxjs/toolkit';

import type {
  MenuLayer,
  PROMOTION_LEVEL,
  SportsMenuPromotedMenuItem,
} from '../types';
import type { ReduxState } from 'app/redux/types';

import type {
  RecordOfMatchEntity,
  RecordOfObjectEntity,
  RecordOfSportEntity,
  RecordOfStageEntity,
  RecordOfTournamentEntity,
  RecordOfUnionEntity,
} from 'app/lib/betting/betting-entities';

/**
 * Betting entity types supported by the menu.
 */
export type SupportedBettingEntity = (
  RecordOfSportEntity |
  RecordOfUnionEntity |
  RecordOfTournamentEntity |
  RecordOfStageEntity |
  RecordOfMatchEntity |
  RecordOfObjectEntity
);

/**
 * Create entity layers selector.
 *
 * Created selector will return a list of layers created
 * from entities that returned by a given `entitiesSelector`.
 */
export const createEntityLayersSelector = <
  E extends SupportedBettingEntity,
  L extends MenuLayer
  >(
    entitiesSelector: Selector<ReduxState, ImmutableMap<number, E>>,
    layerFactory: (entity: E) => L,
  ): Selector<ReduxState, L[]> => {
  return createSelector(
    [entitiesSelector],
    (collection) => {
      return collection
        .valueSeq()
        .toArray()
        .map((entity) => layerFactory(entity));
    },
  );
};

export { createEntityMenuItemsSelector } from './entity-menu-items-selector-factory';

export const createEntityMenuItemsByPromotionLevelSelector = <
  M extends ImmutableMap<number, SupportedBettingEntity>,
  I extends SportsMenuPromotedMenuItem
  >(
    parentEntitiesMapSelector: Selector<ReduxState, M>,
    entityMenuItemsSelector: Selector<ReduxState, I[]>,
    promotionsLevels: PROMOTION_LEVEL[],
    captionAccessor: (parentsMap: M, item: I) => string | undefined,
  ): Selector<ReduxState, Map<PROMOTION_LEVEL, I[]>> => {
  return createSelector(
    parentEntitiesMapSelector,
    entityMenuItemsSelector,
    (parentEntitiesMap, items) => {
      const map = promotionsLevels.reduce((map, level) => {
        map.set(level, []);
        return map;
      }, new Map<PROMOTION_LEVEL, I[]>());

      for (const item of items) {
        map.get(item.entity.promotionLevel)?.push({
          ...item,
          shortcut: true,
          caption: captionAccessor(parentEntitiesMap, item),
        });
      }

      return map;
    },
  );
};

export const groupBy = <T>(
  accessKey: (item: T) => number,
  list: T[],
  map = new Map<number, T[]>(),
): Map<number, T[]> => {
  return list.reduce((map, item) => {
    const key = accessKey(item);

    let list = map.get(key);

    if (!list) {
      list = [];
      map.set(key, list);
    }

    list.push(item);

    return map;
  }, map);
};

export const applyNavigationState = <L extends MenuLayer>(
  layers: L[],
  visibleLayerKey: string | null,
  activeItemKey: string | null,
): L[] => {
  const visibleLayer = (visibleLayerKey && (
    layers.find((layer) => (layer.key === visibleLayerKey))
  ));

  if (!visibleLayer) {
    return layers;
  }

  const activeItem = (activeItemKey && (
    visibleLayer.items
      .find((item) => (item.key === activeItemKey))
  ));

  if (!activeItem) {
    return layers.map((layer) => {
      if (layer === visibleLayer) {
        return {
          ...visibleLayer,
          visible: true,
        };
      }
      return layer;
    });
  }

  return layers.map((layer) => {
    if (layer === visibleLayer) {
      return {
        ...visibleLayer,
        visible: true,
        items: visibleLayer.items.map((item) => {
          if (item === activeItem) {
            return {
              ...item,
              active: true,
            };
          }
          return item;
        }),
      };
    }
    return layer;
  });
};
